import React, { Children, useState, useMemo, useEffect } from 'react';
import { IconChevronLeft, IconChevronRight } from './Icons';
import * as styles from './styles.scss';

const Carousel = ({
    className,
    children,
    showSlides = 6,
    isShowPages = false,
    isShowArrows = true,
    isAbsoluteArrows = true,
    isAutoSlide = false,
}) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [transitions, setTransitions] = useState({});
    const [isReady, setIsReady] = useState(true);
    const slidesCount = useMemo(() => Children.count(children),[children]);
    const [onTouch, setOnTouch] = useState({});

    const handleOnTouchStart = (x, y) => setOnTouch({ x, y });

    const handleOnTouchEnd = (x, y) => {
        const deltaX = onTouch.x - x;
        const deltaY = Math.abs(onTouch.y - y);

        if (Math.abs(deltaX) > 3 * deltaY) {
            if (deltaX < 0) {
                handleLeftClick();
            }

            if (deltaX > 0) {
                handleRightClick();
            }
        }

        setOnTouch({});
    };

    const handleClick = (side) => {
        const nextSlide = side > 0
            ? currentSlide + side
            : currentSlide + side - showSlides;
        const absNextSlide = Math.abs(nextSlide);
        const countOfInsert = Math.trunc(absNextSlide / slidesCount);
        const slide = absNextSlide - countOfInsert * slidesCount;
        const nextIndexSlide = nextSlide < 0
            ? slide
            : slide === 0 ? 0 : slidesCount - slide;
        const dd = nextSlide > 0
            ? countOfInsert - absNextSlide / slidesCount === 0
                ? countOfInsert
                : countOfInsert + 1
            : countOfInsert;
        const transition = dd * slidesCount * 100; // step = slideCount * 100

        if (transition !== Math.abs(transitions[nextIndexSlide])) {
            setTransitions({
                ...transitions,
                [nextIndexSlide]: nextSlide > 0 ? - transition : transition
            });
        }

        setCurrentSlide(currentSlide + side);
        setIsReady(false);
        setTimeout(() => setIsReady(true), 500)
    };

    const handleLeftClick = () => isReady && handleClick(1);
    const handleRightClick = () => isReady && handleClick(-1);

    const renderSlidesStyles = () => ({
        transform: `translateX(${currentSlide * (100 / showSlides)}%)`,
        transition: `transform ${currentSlide === 0 && !Object.keys(transitions).length ? 0 : 0.5}s ease`
    });

    const renderSlideStyles = (index) => {
        const width = `${100 / showSlides}%`;

        return {
            flex: `0 0 ${width}`,
            maxWidth: width,
            transform: `translateX(${transitions[index] || 0}%)`
        }
    };

    const renderSlides = () => Children.map(
        children,
        (child, index) => <div style={renderSlideStyles(index)}>{child}</div>
    );

    const renderLeftButton = () => isShowArrows && (
        slidesCount > showSlides && (
            <button
                className={isAbsoluteArrows ? styles.la : styles.lr }
                onClick={handleLeftClick}
                aria-label="Left"
            >
                <IconChevronLeft />
            </button>
        )
    );

    const renderRightButton = () => isShowArrows && (
        slidesCount > showSlides && (
            <button
                className={isAbsoluteArrows ? styles.ra : styles.rr}
                onClick={handleRightClick}
                aria-label="Right"
            >
                <IconChevronRight />
            </button>
        )
    );

    const renderPages = () => Children.map(children, (child, index) => {
        const indexSlide = Math.abs(Math.trunc(currentSlide / slidesCount) * slidesCount - currentSlide);

        return (
            <button
                onClick={() => isReady && handleClick(indexSlide - index)}
                className={index === indexSlide ? styles.active : undefined}
                aria-label="Slide"
            />
        )
    });
    useEffect(() => {
        const intervalId = isAutoSlide ? setTimeout(() => handleClick(-1), 8000) : null;

        return () => { clearTimeout(intervalId); };
    }, [currentSlide]);

    return (
        <div
            className={`${styles.slider}${className ? ` ${className}` : ''}`}
            onTouchStart={event => handleOnTouchStart(
                event.touches[0].clientX,
                event.touches[0].clientY,
            )}
            onTouchEnd={event => handleOnTouchEnd(
                event.changedTouches[0].clientX,
                event.changedTouches[0].clientY,
            )}
        >
            {renderLeftButton()}
            <div className={styles.wrapper}>
                <div className={styles.slides} style={renderSlidesStyles()}>
                    {renderSlides()}
                </div>
                {
                    isShowPages &&
                    <div className={styles.pages}>
                        {renderPages()}
                    </div>
                }
            </div>
            {renderRightButton()}
        </div>
    );
};

export default Carousel;
